import { cn, cva, type VariantProps } from 'noddi-ui-common';
import { forwardRef, useId } from 'react';
import { RadioGroup, RadioGroupItem } from '../../base/radio-group';
import { webButtonStyling } from '../../helpers/webButtonStyling';
import { NoddiTooltip } from '../Overlay/NoddiTooltip';

export type NoddiRadioOption<ValueType = string | number> = {
  label: string;
  value: ValueType;
  description?: string;
  disabled?: boolean;
};

const radioGroupVariants = cva('w-full', {
  variants: {
    size: {
      small: 'gap-1',
      default: 'gap-1',
      large: 'gap-2'
    },
    layout: {
      default: 'grid grid-cols-1',
      box: 'grid grid-cols-1'
    }
  },
  defaultVariants: {
    size: 'default',
    layout: 'default'
  }
});

const radioItemVariants = cva('', {
  variants: {
    size: {
      small: 'size-3',
      default: 'size-4',
      large: 'size-5'
    }
  },
  defaultVariants: {
    size: 'default'
  }
});

const textVariants = cva('', {
  variants: {
    size: {
      small: 'text-sm',
      default: 'text-sm',
      large: 'text-lg'
    }
  },
  defaultVariants: {
    size: 'default'
  }
});

// The muted text token, checked at 4.5:1 per brand: grey at 70% came to about 2.6:1 on white.
const descriptionVariants = cva('mt-1 text-muted-foreground', {
  variants: {
    size: {
      small: 'text-xs',
      default: 'text-sm',
      large: 'text-base'
    }
  },
  defaultVariants: {
    size: 'default'
  }
});

export type NoddiRadioGroupProps<ValueType = string | number> = VariantProps<typeof radioGroupVariants> & {
  options?: NoddiRadioOption<ValueType>[];
  value?: ValueType;
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  onValueChange?: (value: ValueType) => void;
  defaultValue?: ValueType;
  className?: string;
  disabled?: boolean;
  children?: React.ReactNode;
  name?: string; // Add name prop for unique ID generation
  label?: string;
  tooltip?: string;
  wrapperClassName?: string;
};

export const NoddiRadioGroup = forwardRef<HTMLDivElement, NoddiRadioGroupProps<string | number>>(
  <ValueType extends string | number = string>(
    {
      options,
      value,
      onValueChange,
      defaultValue,
      className,
      id,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      disabled = false,
      layout = 'default',
      size = 'default',
      children,
      name,
      label,
      tooltip,
      wrapperClassName,
      ...props
    }: NoddiRadioGroupProps<ValueType>,
    ref: React.ForwardedRef<HTMLDivElement>
  ) => {
    // Generate a unique ID if no name is provided
    const generatedId = useId();
    const uniqueId = id ?? name ?? generatedId;
    const labelIdentifier = `${uniqueId}-label`;

    // Helper function to safely convert value to string for RadioGroup
    const getRadioValue = (val: ValueType | undefined): string | undefined => {
      return val !== undefined && val !== null ? String(val) : undefined;
    };

    const handleValueChange = (newValue: string) => {
      if (onValueChange) {
        const option = options?.find((opt) => String(opt.value) === newValue);
        if (option) {
          onValueChange(option.value);
        } else {
          onValueChange(newValue as ValueType);
        }
      }
    };

    // Wrapper function to handle label and tooltip
    const renderWithLabelAndTooltip = (radioGroupContent: React.ReactNode) => {
      if (!label && !tooltip) {
        return radioGroupContent;
      }

      return (
        <div className={cn('flex flex-col gap-0.5', wrapperClassName)}>
          {label && (
            <div className='flex items-center gap-2'>
              <label id={labelIdentifier} htmlFor={uniqueId} className='text-sm'>
                {label}
              </label>
              {tooltip && <NoddiTooltip content={tooltip} label={`More information about ${label}`} />}
            </div>
          )}
          {radioGroupContent}
        </div>
      );
    };

    // If children are provided, render the simple case
    if (children) {
      const radioGroupContent = (
        <RadioGroup
          ref={ref}
          id={uniqueId}
          aria-labelledby={label ? labelIdentifier : undefined}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          name={name}
          value={getRadioValue(value)}
          defaultValue={getRadioValue(defaultValue)}
          onValueChange={handleValueChange}
          disabled={disabled}
          className={cn(radioGroupVariants({ size }), className)}
          {...props}
        >
          {children}
        </RadioGroup>
      );

      return renderWithLabelAndTooltip(radioGroupContent);
    }

    // If options are provided, render the enhanced case
    if (options) {
      const radioGroupContent = (
        <RadioGroup
          ref={ref}
          id={uniqueId}
          aria-labelledby={label ? labelIdentifier : undefined}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          name={name}
          value={getRadioValue(value)}
          defaultValue={getRadioValue(defaultValue)}
          onValueChange={handleValueChange}
          disabled={disabled}
          className={cn(radioGroupVariants({ size, layout }), className)}
          {...props}
        >
          {options.map((option) => {
            if (layout === 'box') {
              const radioId = `${uniqueId}-${String(option.value)}`;
              const isOptionDisabled = option.disabled || disabled;
              return (
                <label
                  key={String(option.value)}
                  htmlFor={radioId}
                  className={cn(
                    'group relative block rounded-lg border border-outlineStroke transition-colors',
                    'focus-within:bg-grey/5 hover:bg-grey/5',
                    isOptionDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                    webButtonStyling
                  )}
                >
                  <RadioGroupItem
                    id={radioId}
                    aria-describedby={option.description ? `${radioId}-description` : undefined}
                    value={String(option.value)}
                    disabled={isOptionDisabled}
                    className={cn('absolute top-4 left-4', radioItemVariants({ size }))}
                  />
                  <div className='flex items-start p-3 pl-12'>
                    <div className='min-w-0 flex-1'>
                      <span className={cn('block font-medium text-black', textVariants({ size }))}>{option.label}</span>
                      {option.description && (
                        <p id={`${radioId}-description`} className={descriptionVariants({ size })}>
                          {option.description}
                        </p>
                      )}
                    </div>
                  </div>
                </label>
              );
            }

            // Default layout - simple flex layout with clickable area
            const radioId = `${uniqueId}-${String(option.value)}`;
            return (
              <div
                key={String(option.value)}
                className={cn(
                  'group relative flex items-center rounded-md transition-colors',
                  'focus-within:bg-grey/5 hover:bg-grey/5',
                  (option.disabled || disabled) && 'cursor-not-allowed opacity-50'
                )}
              >
                <RadioGroupItem
                  id={radioId}
                  aria-describedby={option.description ? `${radioId}-description` : undefined}
                  value={String(option.value)}
                  disabled={option.disabled || disabled}
                  className={cn('ml-2', radioItemVariants({ size }))}
                />
                <label
                  className={cn(
                    'flex-1 cursor-pointer p-2',
                    textVariants({ size }),
                    (option.disabled || disabled) && 'cursor-not-allowed'
                  )}
                  htmlFor={radioId}
                >
                  {option.label}
                  {option.description && (
                    <span id={`${radioId}-description`} className={cn('block', descriptionVariants({ size }))}>
                      {option.description}
                    </span>
                  )}
                </label>
              </div>
            );
          })}
        </RadioGroup>
      );

      return renderWithLabelAndTooltip(radioGroupContent);
    }

    // Fallback - render empty RadioGroup
    const fallbackContent = (
      <RadioGroup
        ref={ref}
        id={uniqueId}
        aria-labelledby={label ? labelIdentifier : undefined}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        name={name}
        value={getRadioValue(value)}
        defaultValue={getRadioValue(defaultValue)}
        onValueChange={handleValueChange}
        disabled={disabled}
        className={cn(radioGroupVariants({ size }), className)}
        {...props}
      />
    );

    return renderWithLabelAndTooltip(fallbackContent);
  }
);

NoddiRadioGroup.displayName = 'NoddiRadioGroup';
